import PropTypes from 'prop-types';
import React from 'react';

import Html from 'Component/Html';
import Link from 'Component/Link';
import Loader from 'Component/Loader';
import PageCard from 'Component/PageCard';
import ProductCard from 'Component/ProductCard';
import SearchResultsCategories from 'Component/SearchResultsCategories';
import SearchResultsTabs from 'Component/SearchResultsTabs';
import { SEARCH_FIELD_OTHERS, SEARCH_FIELD_PAGES, SEARCH_FIELD_PRODUCTS } from 'Query/Search.query';
import {
    SearchOverlay as SourceSearchOverlay
} from 'SourceComponent/SearchOverlay/SearchOverlay.component';
import { SearchResultsType } from 'Type/Search.type';
import { addInsecableSpaceBeforePunctuation } from 'Util/Common';
import { appendWithStoreCode } from 'Util/Url';

import './SearchOverlay.override.style';

/** @namespace PeggysagePwa/Component/SearchOverlay/Component */
export class SearchOverlayComponent extends SourceSearchOverlay {
    static propTypes = {
        ...SourceSearchOverlay.propTypes,
        searchResults: SearchResultsType.isRequired,
        activeTab: PropTypes.string,
        onTabClick: PropTypes.func.isRequired
    };

    static defaultProps = {
        activeTab: null
    };

    otherSearchResultsTypesMap = {
        page: {
            render: this.renderSearchResultsTabListItemPage.bind(this)
        },
        news: {
            render: this.renderSearchResultsTabListItemPage.bind(this)
        },
        faq: {
            render: this.renderSearchResultsTabListItemFaq.bind(this)
        }
    };

    /**
     * Override: move to container to replace setTimeout by debounce
     */
    // eslint-disable-next-line @scandipwa/scandipwa-guidelines/only-render-in-component
    componentDidUpdate() {
        // ...
    }

    renderSearchResultsCategories() {
        const { searchResults } = this.props;

        const filterCategory = searchResults.find(
            (searchResult) => searchResult.type === SEARCH_FIELD_PRODUCTS
        )?.filters?.find(
            (filter) => filter.request_var === 'category_id'
        ) ?? {};

        return <SearchResultsCategories filter={ filterCategory } />;
    }

    renderSearchResultsTabs() {
        const { searchResults, activeTab, onTabClick } = this.props;

        if (!searchResults || !searchResults.length) {
            return null;
        }

        return (
            <SearchResultsTabs
              searchResults={ searchResults }
              activeTab={ activeTab }
              onTabClick={ onTabClick }
            />
        );
    }

    /**
     * @param {object} item
     */
    renderSearchResultsTabListItemPage(item) {
        const { id, title, url } = item;

        return (
            <div block="SearchOverlay" elem="TabListItem" mods={ { page: true } } key={ id }>
                <Link to={ url }>
                    <Html content={ title } />
                </Link>
            </div>
        );
    }

    /**
     * @param {object} item
     */
    renderSearchResultsTabListItemFaq(item) {
        const { id, faq_category_id, title } = item;

        const faqId = id.split('faq_').at(-1);
        const url = appendWithStoreCode(`/faq/${faq_category_id}#${faqId}`);

        return (
            <div block="SearchOverlay" elem="TabListItem" mods={ { faq: true } } key={ id }>
                <Link to={ url }>
                    <Html content={ addInsecableSpaceBeforePunctuation(title) } />
                </Link>
            </div>
        );
    }

    /**
     *
     * @param {string} type
     * @param {object} item
     * @returns {JSX.Element|null|*}
     */
    renderSearchResultsTabListItem(type, item) {
        const mix = {
            block: 'SearchOverlay',
            elem: 'TabListItem'
        };

        switch (type) {
        case SEARCH_FIELD_PRODUCTS:
            return (
                <ProductCard
                  product={ item }
                  isSkuVisible
                  mix={ mix }
                  key={ item.id }
                />
            );
        case SEARCH_FIELD_PAGES:
            return (
                <PageCard
                  page={ item }
                  isHorizontallyDisplayedOnMobile
                  isHorizontallyDisplayedOnDesktop
                  isLinkVisible
                  isListing
                  mix={ mix }
                  key={ item.identifier }
                />
            );
        case SEARCH_FIELD_OTHERS:
            const { content_type } = item;

            const renderFunc = this.otherSearchResultsTypesMap[content_type]?.render;

            if (!renderFunc) {
                return null;
            }

            return renderFunc(item);
        default:
            return null;
        }
    }

    renderSearchResultsTabListItems(searchResult) {
        const { type, items } = searchResult;

        if (type === SEARCH_FIELD_OTHERS) {
            const itemsByContentType = items.reduce((acc, item) => {
                const { content_type } = item;

                if (!acc[content_type]) {
                    acc[content_type] = [];
                }

                acc[content_type].push(item);

                return acc;
            }, {});

            return Object.entries(itemsByContentType).map(([contentType, items]) => {
                const title = (() => {
                    switch (contentType) {
                    case 'faq':
                        return __('FAQ');
                    case 'news':
                        return __('News');
                    default:
                        return __('Pages');
                    }
                })();

                return (
                    <div block="SearchOverlay" elem="TabListItemGroup">
                        <h3 block="SearchOverlay" elem="TabListItemGroupTitle">
                            { title }
                        </h3>
                        <div block="SearchOverlay" elem="TabListItemGroupContent">
                            { items.map(this.renderSearchResultsTabListItem.bind(this, type)) }
                        </div>
                    </div>
                );
            });
        }

        return items.map(this.renderSearchResultsTabListItem.bind(this, type));
    }

    /**
     * @param {SearchResultType} searchResult
     * @returns {JSX.Element|null}
     */
    renderSearchResultsTabListButton(searchResult) {
        const { items, total_count } = searchResult;
        const { searchCriteria, activeTab } = this.props;

        if (!items || items.length >= total_count) {
            return null;
        }

        const url = appendWithStoreCode(`/search/${searchCriteria}`);

        // We pass the selected tab to the link, in order to retrieve it in the search page (in location var)
        const linkTo = {
            pathname: url,
            state: { activeTab }
        };

        return (
            <div block="SearchOverlay" elem="TabListButton">
                <Link
                  to={ linkTo }
                  block="Button"
                >
                    { __('Voir tous les résultats') }
                </Link>
            </div>
        );
    }

    renderSearchResultsTabList() {
        const { searchResults, activeTab } = this.props;

        const searchResult = searchResults.find((searchResult) => searchResult.type === activeTab);

        if (!searchResult) {
            return null;
        }

        const { type, items } = searchResult;

        if (!items || !items.length) {
            return null;
        }

        return (
            <div block="SearchOverlay" elem="TabList">
                <div block="SearchOverlay" elem="TabListContent" mods={ { type } }>
                    { this.renderSearchResultsTabListItems(searchResult) }
                </div>
                { this.renderSearchResultsTabListButton(searchResult) }
            </div>
        );
    }

    /**
     * Override
     */
    renderSearchResults() {
        const { searchResults, isLoading } = this.props;

        if (isLoading || this.timeout) {
            return (
                <div block="SearchOverlay" elem="Loader">
                    <Loader isLoading />
                </div>
            );
        }

        const totalCount = searchResults.reduce(
            (acc, searchResult) => acc + searchResult.total_count,
            0
        );

        if (!totalCount && !isLoading && !this.timeout) {
            return this.renderNoResults();
        }

        return (
            <>
                { this.renderSearchResultsCategories() }
                { this.renderSearchResultsTabs() }
                { this.renderSearchResultsTabList() }
            </>
        );
    }
}

export default SearchOverlayComponent;
